import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface AguardarAssinaturaParams {
  flowId: string;
  onConfirm: (data: any) => Promise<void>;
  onCreate?: (data: any) => Promise<void>;
  timeoutMs?: number;
}

interface UseWebSocketAssinaturaReturn {
  aguardarAssinatura: (params: AguardarAssinaturaParams) => Promise<void>;
}

export function useWebSocketAssinatura(): UseWebSocketAssinaturaReturn {
  const socketRef = useRef<Socket | null>(null);

  const aguardarAssinatura = async ({
    flowId,
    onConfirm,
    onCreate,
    timeoutMs = 120000,
  }: AguardarAssinaturaParams): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const socket = io(process.env.REACT_APP_WS_URL as string, {
        transports: ['websocket'],
      });
      const timeout = window.setTimeout(() => {
        socket.disconnect();
        reject(
          new Error(
            'Tempo esgotado aguardando a confirmacao da assinatura. Tente novamente.',
          ),
        );
      }, timeoutMs);

      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join-room', { room: flowId });
      });

      const handleConfirm = async (
        payload: any,
        callback?: (data: any) => void,
      ) => {
        callback?.({
          received: true,
          at: new Date().toISOString(),
        });

        try {
          await onConfirm(payload);
          window.clearTimeout(timeout);
          socket.disconnect();
          resolve();
        } catch (err) {
          window.clearTimeout(timeout);
          socket.disconnect();
          reject(err);
        }
      };

      socket.on('assinatura-confirmada', handleConfirm);
      socket.on('inclusao-associado', handleConfirm);

      socket.on('confirmation:created', async payload => {
        if (!onCreate) {
          return;
        }

        try {
          await onCreate(payload);
          window.clearTimeout(timeout);
          socket.disconnect();
          resolve();
        } catch (err) {
          window.clearTimeout(timeout);
          socket.disconnect();
          reject(err);
        }
      });

      socket.on('connect_error', err => {
        window.clearTimeout(timeout);
        socket.disconnect();
        reject(err);
      });
    });
  };

  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  return { aguardarAssinatura };
}
